import { useEffect, useRef } from 'react';
import type { UpgradeHint } from '@snap-platform/shared';
import { ShieldCheckIcon, AlertTriangleIcon, PlugIcon } from './Icons.js';

interface UpgradePromptModalProps {
  hint: UpgradeHint;
  onUpgrade: () => void;
  onDismiss: () => void;
}

type Urgency = 'high' | 'medium' | 'low';

function sanitizeDescription(text: string): string {
  return text.replace(/real-time alerts/gi, 'risk-based warnings at scan time');
}

function getHintContent(hint: UpgradeHint): {
  title: string;
  description: string;
  urgency: Urgency;
  ctaLabel: string;
} {
  switch (hint.type) {
    case 'usage_limit':
      if (hint.priority === 'high') {
        return {
          title: "You've reached your free scan limit",
          description: sanitizeDescription(hint.message),
          urgency: 'high',
          ctaLabel: 'View Pricing',
        };
      }
      return {
        title: 'Running low on scans',
        description: sanitizeDescription(hint.message),
        urgency: 'medium',
        ctaLabel: 'View Pricing',
      };
    case 'risk_protection':
      return {
        title: 'Advanced protection available',
        description: sanitizeDescription(hint.message),
        urgency: hint.priority === 'high' ? 'high' : 'medium',
        ctaLabel: 'View Pricing',
      };
    case 'high_value_protection':
      return {
        title: 'Enhanced protection for high-value transactions',
        description: sanitizeDescription(hint.message),
        urgency: 'medium',
        ctaLabel: 'View Pricing',
      };
    case 'version_update':
      return {
        title: 'Snap Update Available',
        description: sanitizeDescription(hint.message),
        urgency: 'low',
        ctaLabel: 'Update Snap',
      };
    case 'feature_gate':
      return {
        title: 'Unlock Pro Features',
        description: sanitizeDescription(hint.message),
        urgency: 'low',
        ctaLabel: 'View Pricing',
      };
  }
}

export function UpgradePromptModal({
  hint,
  onDismiss,
}: UpgradePromptModalProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<Element | null>(null);

  const { title, description, urgency, ctaLabel } = getHintContent(hint);
  const isAlert = urgency === 'high' || urgency === 'medium';

  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    cardRef.current?.focus();

    return () => {
      if (previousFocusRef.current instanceof HTMLElement) {
        previousFocusRef.current.focus();
      }
    };
  }, []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onDismiss();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onDismiss]);

  function handleOverlayClick(e: React.MouseEvent) {
    if (e.target === e.currentTarget) {
      onDismiss();
    }
  }

  function getIcon() {
    if (hint.type === 'version_update') {
      return <PlugIcon className="upm-icon upm-icon-plug" aria-hidden="true" />;
    }
    if (isAlert) {
      return <AlertTriangleIcon className="upm-icon upm-icon-alert" aria-hidden="true" />;
    }
    return <ShieldCheckIcon className="upm-icon upm-icon-shield" aria-hidden="true" />;
  }

  return (
    <div className="upm-overlay" onClick={handleOverlayClick}>
      <div
        className={`upm-card upm-card-${urgency}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upm-title"
        ref={cardRef}
        tabIndex={-1}
      >
        <button className="upm-close" onClick={onDismiss} aria-label="Close">
          &times;
        </button>

        <div className="upm-icon-wrap">
          {getIcon()}
        </div>

        <h2 className="upm-title" id="upm-title">
          {title}
        </h2>
        <p className="upm-desc">{description}</p>

        <div className="upm-actions">
          <a className="btn btn-primary btn-primary-glow" href="/pricing">
            {ctaLabel}
          </a>
          <button className="btn btn-secondary" onClick={onDismiss}>
            Maybe Later
          </button>
        </div>

        <p className="upm-trust">Upgrade to unlock full protection.</p>
      </div>
    </div>
  );
}
